import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FieldRow, Remaining } from "./Champ";
import { allInvalid, type Complaint } from "./checks";
import { visibleFields } from "./conditions";
import { useEvent } from "../context/EventProvider";
import { logSlotChanges, slotChanges } from "../log/conferences";
import {
  toggle,
  isSelected,
  salesDays,
  fetchArticles,
  saveSelection,
  type Selection,
  cleanSelection,
} from "./articles";
import { ArticlePrice } from "./ArticlePrice";
import { useAnswers } from "./answers";
import { carrierHeritage, hasArticles, writeDays } from "./companions";
import { attach, fetchCompanionCompanies } from "./companies";
import { companionCompany } from "./companionCompany";
import { COMPANY_STEP, type CompanionStep } from "./companionThread";
import {
  fetchConferences,
  maxAnnounce,
  placeSlot,
  programme,
  type Slot,
} from "./conferences";
import { ConferencePicker } from "./ConferencePicker";
import { longDay } from "./dates";
import { fetchAnswers, saveAnswers, valueOf, type Field } from "./forms";
import type { Words } from "./formI18n";
import { fetchSelection } from "./articles";
import { fetchParticipation } from "./participant";
import { useParticipation } from "./useParticipation";
import { useCompany } from "./useSociete";
import { logAction } from "../log/actionLog";
import {
  useFlagOr,
  useLocale,
  useSettings,
  useText,
  useT,
} from "../settings/context";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Prose } from "../ui/Prose";

/**
 * CE QU'ON REMPLIT POUR UN ACCOMPAGNANT, UNE ÉTAPE À LA FOIS.
 *
 * ⚠️ LES ÉCRANS DU PORTEUR NE SONT PAS RÉUTILISÉS, ET CE N'EST PAS UN OUBLI. Ils sont
 * construits autour de `me` — la personne connectée — et de l'état de navigation du
 * tunnel : les faire servir quelqu'un d'autre demanderait de les paramétrer par
 * identifiant de bout en bout, y compris leur fil d'Ariane et leur garde d'entrée.
 * Ce qui EST réutilisé, c'est tout ce qui compte : les fonctions de données, qui
 * prennent déjà un `userId` (`fetchConferences`, `poseCreneau`, `enregistreSelection`),
 * et les règles d'affichage (`etalage`, `programme`, `jourLong`). Aucune règle métier
 * n'est réécrite ici.
 *
 * ⚠️ CHAQUE ÉTAPE ÉCRIT EN LA QUITTANT. La personne existe déjà — elle a été créée par
 * le formulaire — donc il n'y a rien à retenir jusqu'à un hypothétique bout : ce qu'on
 * coche pour elle la concerne tout de suite. C'est aussi ce qui rend l'abandon
 * inoffensif : on s'arrête, elle reste inscrite avec ce qu'on lui a donné.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback: what the site says of itself follows the language being read.
 */

/** The company choice, focused when it is the one left unanswered. */
const COMPANY_CHOICE = "f-companion-company";

export function CompanionSteps({
  funnel,
  index,
  companionId,
  name,
  fields,
  words,
  onGo,
  onCreate,
  onNext,
  onDone,
  onCancel,
  attachToCompany = false,
}: {
  /** Tout ce qu'on remplira pour cette personne — le fil s'en dessine. */
  funnel: ReadonlyArray<CompanionStep>;
  /** Où l'on en est dedans. */
  index: number;
  /**
   * ⚠️ `null` TANT QU'ELLE N'EXISTE PAS. On entre dans ce parcours AVANT d'avoir créé
   * qui que ce soit : c'est la première étape — le formulaire — qui la fait naître, et
   * les suivantes n'ont de sens qu'après. Toutes les lectures d'ici sont donc
   * conditionnées à cet identifiant.
   */
  companionId: number | null;
  /** Vide tant qu'on ne l'a pas nommée. */
  name: string;
  /** Son formulaire, tel que l'étape le lui sert. */
  fields: Field[];
  words: Words | undefined;
  /** Revenir sur une étape déjà franchie. */
  onGo: (index: number) => void;
  /**
   * CRÉE LA PERSONNE, et rend son identifiant.
   *
   * ⚠️ LE PARENT S'EN CHARGE, pas cet écran : la création touche à la liste, au plafond
   * et à ce que la confirmation rappellera — c'est l'étape qui en est responsable. Ici
   * on ne fait que recueillir le formulaire et demander.
   */
  onCreate: (values: Record<string, unknown>) => Promise<number | null>;
  onNext: () => void;
  onDone: () => void;
  /**
   * REFERMER LE PARCOURS SANS LE FINIR, et sans rien écrire (bug #339).
   *
   * ⚠️ CE QUI EST DÉJÀ ENREGISTRÉ LE RESTE. Une personne créée à la première étape
   * reste inscrite, dans la liste, avec Modifier et Retirer ; seule la saisie de
   * l'étape affichée est abandonnée.
   */
  onCancel: () => void;
  /**
   * THIS PERSON COMES UNDER ONE OF THE BEARER'S COMPANIES (`funnel/companionCompany.ts`):
   * automatically with one, by the company step with several. Set by the companions step
   * when the organiser offers coworkers, or asks for it (`attachesCompany`).
   */
  attachToCompany?: boolean;
}) {
  const t = useT();
  const step = funnel[index]!;
  const remaining = funnel.length - index - 1;
  const event = useEvent();
  const { days } = useSettings();
  /* Le PORTEUR : ce qu'il a répondu pour lui-même sert de point de départ à ce qu'on
     remplit pour les siens. */
  const { me, recorded } = useParticipation();
  /* ⚠️ ET CE QU'IL VIENT DE CHOISIR, QUI N'EST PAS ENCORE ÉCRIT : à la première
     inscription, rien d'autre n'existe (bug #200, voir `carrierHeritage`). */
  const [answers] = useAnswers();
  const heritage = carrierHeritage({
    answers,
    recorded,
    all: days.map((d) => d.id),
  });
  const reuses = useFlagOr("FO_MEETING_V2_COMPANION_COPY");
  /* La commande du porteur n'est à lire sur le serveur que si son brouillon n'en porte
     pas : c'est alors l'enregistrée qui fait foi. */
  const readsTheirCart = reuses && heritage.articles === undefined;
  /* Le mot de l'organisateur au-dessus de SON formulaire — il suit le formulaire, donc
     il a suivi ici quand celui-ci est entré dans le parcours. */
  const intro = useText("FO_MEETING_V2_COMPANION_HTML");
  const client = useQueryClient();
  const [sending, setSending] = useState(false);
  const [rate, setRate] = useState(false);

  const locale = useLocale();

  const advance = () => {
    /* ⚠️ ON REPART SANS LA PHRASE. Cet écran ne se démonte pas entre deux étapes ni
       entre deux personnes : gardée, elle se serait affichée sur le formulaire VIERGE du
       deuxième accompagnant, qui n'a rien tenté du tout. */
    setTried(false);
    return remaining > 0 ? onNext() : onDone();
  };

  /**
   * ── CE QU'IL A DÉJÀ ──
   *
   * ⚠️ CHAQUE ÉTAPE S'OUVRE SUR L'EXISTANT, sans quoi « modifier » écraserait au lieu de
   * corriger : revenir sur ses jours pour en ajouter un les aurait tous remis à zéro,
   * et repasser par sa billetterie aurait vidé sa commande. Les trois lectures sont
   * conditionnées à l'étape affichée — on ne paie que ce qu'on montre.
   */
  const alreadyDays = useQuery({
    queryKey: [
      "participation",
      event?.domainId,
      event?.eventId,
      companionId ?? "neuve",
    ],
    queryFn: ({ signal }) =>
      fetchParticipation(event!.domainId, event!.eventId, companionId!, signal),
    enabled: step.key === "dates" && !!event && companionId !== null,
    staleTime: Infinity,
    retry: false,
  });
  const alreadyCart = useQuery({
    queryKey: [
      "articles-retenus",
      event?.domainId,
      event?.eventId,
      companionId ?? "neuve",
    ],
    queryFn: ({ signal }) =>
      fetchSelection(event!.domainId, event!.eventId, companionId!, signal),
    enabled: step.key === "articles" && !!event && companionId !== null,
    staleTime: Infinity,
    retry: false,
  });
  /**
   * ⚠️ LA COMMANDE DU PORTEUR, POUR AMORCER CELLE DE SON ACCOMPAGNANT. On vient
   * ensemble, on prend souvent la même chose — et sans elle, l'étape s'ouvrait vide
   * alors que tout le reste du parcours reprend ses choix.
   *
   * ⚠️ ELLE ENGAGE DE L'ARGENT, ET C'EST LE SEUL CAS DU PARCOURS. Ces articles tombent
   * sur la facture du porteur : le serveur additionne ceux de toutes les personnes qu'il
   * inscrit. D'où deux précautions — elle n'amorce QUE si la personne n'a rien retenu,
   * et l'étape le dit en toutes lettres au lieu de laisser découvrir le total à la
   * confirmation.
   */
  const theirCart = useQuery({
    queryKey: ["articles-retenus", event?.domainId, event?.eventId, me?.id],
    queryFn: ({ signal }) =>
      fetchSelection(event!.domainId, event!.eventId, me!.id, signal),
    enabled:
      step.key === "articles" &&
      readsTheirCart &&
      !!event &&
      typeof me?.id === "number",
    staleTime: Infinity,
    retry: false,
  });
  const alreadyAnswers = useQuery({
    queryKey: [
      "form-answers",
      event?.domainId,
      event?.eventId,
      companionId ?? "neuve",
    ],
    queryFn: ({ signal }) =>
      fetchAnswers(
        event!.domainId,
        { eventId: event!.eventId, userId: companionId! },
        signal,
      ),
    enabled: step.key === "informations" && !!event && companionId !== null,
    staleTime: Infinity,
    retry: false,
  });

  /**
   * ── THEIR COMPANY ── (a coworker only)
   *
   * Several companies: asked on a step of its own, first (`COMPANY_STEP`, placed by the
   * companions step). A single one: no step, the form names it. Either way the form
   * writes it for a new person, the moment it creates them; for someone who already
   * exists, leaving the company step writes it.
   *
   * The bearer's companies are the choices: the same read, and the same cache, as the
   * bearer's company step, which comes earlier in the funnel. What the person already
   * has is read only once they exist, to reopen the choice on it.
   */
  const hasCompanyStep = funnel.some((s) => s.key === COMPANY_STEP.key);
  const companyFlow =
    attachToCompany &&
    (step.key === COMPANY_STEP.key || step.key === "informations");
  const bearerCompanies = useCompany(companyFlow);
  const offered = bearerCompanies.companies;
  const readsTheirCompany =
    companyFlow && companionId !== null && offered.length > 0;
  /**
   * ⚠️ READ AGAIN EVERY TIME THE STEP OPENS, NEVER TRUSTED FROM THE CACHE (`staleTime: 0`,
   * and « Continuer » waits for the read, see `readInProgress`). What they have is
   * what decides whether anything is written: a participation remembered from before a
   * « Retirer » showed their old company as chosen, so nothing was written, and they
   * stayed under none. Same key as their days: one request for both.
   */
  const theirRecord = useQuery({
    queryKey: [
      "participation",
      event?.domainId,
      event?.eventId,
      companionId ?? "neuve",
    ],
    queryFn: ({ signal }) =>
      fetchParticipation(event!.domainId, event!.eventId, companionId!, signal),
    enabled: readsTheirCompany && !!event,
    staleTime: 0,
    retry: false,
  });
  const theirCompanies = useQuery({
    queryKey: ["companies", event?.domainId, companionId ?? "neuve"],
    queryFn: ({ signal }) =>
      fetchCompanionCompanies(
        event!.domainId,
        event!.eventId,
        companionId!,
        signal,
      ),
    enabled: readsTheirCompany && !!event,
    staleTime: 0,
    retry: false,
  });
  const { current: currentCompany, preset: presetCompany } = companionCompany({
    offered,
    ...(theirCompanies.data ? { theirs: theirCompanies.data } : {}),
    theirEmployeeId: theirRecord.data?.employeeId ?? null,
  });
  /* `null` until the bearer picks: the choice then shows the preset. */
  const [pickedCompany, setPickedCompany] = useState<number | null>(null);
  /** What this journey wrote: their participation is not read again between two steps. */
  const [writtenCompany, setWrittenCompany] = useState<number | null>(null);
  const chosenCompany =
    pickedCompany !== null && offered.some((c) => c.id === pickedCompany)
      ? pickedCompany
      : presetCompany;
  const chosenName = offered.find((c) => c.id === chosenCompany)?.name;
  /** Several companies, none chosen: said under the choice once « Continuer » is tried. */
  const [companyComplaint, setCompanyComplaint] = useState(false);
  const companyMissing =
    companyFlow && offered.length > 1 && chosenCompany === null;
  /**
   * ⚠️ THE BEARER'S COMPANIES COULD NOT BE READ: NOTHING IS CREATED. Going on would
   * register a coworker under no company without a word, the very thing this choice
   * is for; reloading the page is enough to read them again.
   */
  const companiesUnread = companyFlow && bearerCompanies.failure;

  /* ── SES JOURS ──
     ⚠️ `null` TANT QU'ON N'A PAS TOUCHÉ, pour distinguer « il n'en a aucun » de « on ne
     sait pas encore » : semé à `[]`, tout se décochait sous la main dès que la réponse
     arrivait ; semé à « tous », on lui en ajoutait à son insu. */
  const [pickedDays, setPickedDays] = useState<number[] | null>(null);
  /**
   * ⚠️ TROIS SOURCES, DANS CET ORDRE : ce qu'on vient de cocher, ce que la personne a
   * déjà, puis LES JOURS DU PORTEUR. Cette troisième est celle qui manquait : une étape
   * activée après coup s'ouvrait sur TOUS les jours de l'événement, donc sur des
   * journées où le porteur lui-même ne vient pas. On accompagne quelqu'un — le défaut
   * raisonnable est de venir quand il vient. Ceux de son BROUILLON d'abord, puis les
   * enregistrés : à la première inscription, seul le brouillon les porte (bug #200).
   */
  const takenDays =
    pickedDays ??
    (alreadyDays.data?.days?.length ? alreadyDays.data.days : heritage.days);

  /* ── SA BILLETTERIE ── */
  const [pickedCart, setPickedCart] = useState<Selection | null>(null);
  const theirsAlreadyEmpty =
    alreadyCart.data !== undefined &&
    Object.values(alreadyCart.data).every((l) => l.length === 0);
  /* Amorcé sur celui du porteur seulement si la personne n'a RIEN retenu : sinon on
     ajouterait des articles à une commande existante, donc au montant dû. */
  const reused =
    theirsAlreadyEmpty && reuses
      ? (heritage.articles ?? theirCart.data ?? {})
      : undefined;
  const cart = pickedCart ?? reused ?? alreadyCart.data ?? {};

  /* ── SON FORMULAIRE ── */
  const [typed, setTyped] = useState<Record<string, unknown>>({});
  /**
   * CE QU'ON REPROCHE AUX CHAMPS DE SON FORMULAIRE — dit sous chaque champ visé.
   *
   * ⚠️ IL N'Y EN AVAIT AUCUN, ET RIEN N'ARRÊTAIT PERSONNE. Ces champs sont les mêmes que
   * ceux de l'étape informations du porteur, obligatoires compris ; ils étaient posés ici
   * sans `<form>` — donc même le navigateur n'avait rien à faire respecter — et
   * « Continuer » enregistrait un accompagnant à qui il manquait des réponses exigées.
   */
  const [complaints, setComplaints] = useState<Complaint[]>([]);
  /* A-t-on déjà essayé de valider cette étape ? La phrase du bas ne parle qu'après. */
  const [tried, setTried] = useState(false);
  const { data: articles } = useQuery({
    queryKey: ["articles", event?.domainId, event?.eventId, false],
    queryFn: ({ signal }) =>
      fetchArticles(event!.domainId, event!.eventId, false, signal),
    enabled: step.key === "articles" && !!event,
    staleTime: Infinity,
    retry: false,
  });

  /* ── SES CONFÉRENCES ── */
  const { data: conferences } = useQuery({
    queryKey: [
      "conferences",
      event?.domainId,
      event?.eventId,
      companionId ?? "neuve",
    ],
    queryFn: ({ signal }) =>
      fetchConferences(event!.domainId, event!.eventId, companionId!, signal),
    enabled: step.key === "conferences" && !!event && companionId !== null,
    staleTime: 30_000,
    retry: false,
  });

  /**
   * ═══ CE QU'ON VIENT D'ÉCRIRE N'EST PLUS CE QUI EST EN CACHE ═══
   *
   * ⚠️ ET SANS ÇA, MODIFIER QUELQU'UN NE SE VOYAIT QU'APRÈS UN RECHARGEMENT. Les trois
   * lectures de ce parcours portent `staleTime: Infinity` — c'est voulu, ces réponses ne
   * changent pas toutes seules — ET `enabled` sur l'étape affichée : en quittant l'étape
   * la requête se désactive, en y revenant elle se réactive avec des données que rien
   * n'a marquées périmées, donc elle ne redemande RIEN. On rouvrait le formulaire sur
   * l'état d'avant sa propre correction.
   *
   * Le même piège avait été refermé côté porteur (`routes/Informations.tsx`) ; il était
   * resté ouvert ici. Marquer périmé suffit : la relecture se fait au retour, quand
   * quelqu'un regarde.
   */
  /**
   * ⚠️ LA LECTURE DE L'EXISTANT DE CETTE ÉTAPE DOIT ÊTRE RÉGLÉE AVANT D'ÉCRIRE. « Continuer »
   * part sinon sur un repli — les jours du porteur, un panier vide — et écrase à
   * l'enregistrement ce que la personne avait déjà. En vol, on désactive le bouton ; en
   * échec, on le dit plutôt que de laisser détruire une commande.
   */
  const readInProgress =
    (step.key === "dates" && companionId !== null && alreadyDays.isPending) ||
    (step.key === "articles" &&
      companionId !== null &&
      (alreadyCart.isPending || (readsTheirCart && theirCart.isPending))) ||
    (step.key === "informations" &&
      companionId !== null &&
      alreadyAnswers.isPending) ||
    /* Their company is decided on what both reads say: one company or several, and
       what they already have. */
    (companyFlow && !bearerCompanies.fetched && !bearerCompanies.failure) ||
    /* FETCHING, not pending: a remembered answer is shown while the server is asked
       again, and must not be acted on. */
    (readsTheirCompany && (theirRecord.isFetching || theirCompanies.isFetching));
  const readRate =
    (step.key === "dates" && alreadyDays.isError) ||
    (step.key === "articles" &&
      (alreadyCart.isError || (readsTheirCart && theirCart.isError))) ||
    (step.key === "informations" && alreadyAnswers.isError) ||
    (readsTheirCompany && (theirRecord.isError || theirCompanies.isError));

  const stale = (queryKey: unknown[]) =>
    client.invalidateQueries({ queryKey, refetchType: "none" });

  /**
   * ATTACHES THE PERSON TO THE CHOSEN COMPANY, FOR THIS EVENT.
   *
   * One request for both writes: the route inserts their `Employees` row (or brings it
   * back from the archive) and, given the event, points their participation at it
   * (`UserEvents.employeeId`, what the badge prints). Unlike the bearer's own step
   * (`keepForEvent`), nothing has to follow: the participation always exists here, the
   * companion route creates it with the person.
   *
   * ⚠️ NOTHING IS WRITTEN WHEN THEIR COMPANY IS ALREADY THE CHOSEN ONE: reopening
   * someone to fix their name does not rewrite their attachment. Nor twice: the form
   * that follows the company step reads the same participation, not yet read again, so
   * what this journey wrote is remembered here (`writtenCompany`).
   */
  const attachCompany = async (personId: number) => {
    if (!event || !companyFlow || chosenCompany === null) return;
    if (chosenCompany === (writtenCompany ?? currentCompany)) return;
    await attach(event.domainId, chosenCompany, personId, event.eventId);
    setWrittenCompany(chosenCompany);
    await stale(["participation", event.domainId, event.eventId, personId]);
    await stale(["companies", event.domainId, personId]);
    /* The conference locks depend on the company: one coworker per company, on some
       of them (see `routes/Company.tsx`). */
    await stale(["conferences"]);
    /* They are a coworker now: the list of the step behind must show them. */
    await client.invalidateQueries({ queryKey: ["collaborateurs"] });
    logAction(event, "companyUpdate", {
      userId: personId,
      companyId: chosenCompany,
      label: "Modification",
    });
  };

  const valid = async () => {
    if (!event || sending) return;
    /* La lecture de l'existant n'est pas réglée : ne pas écrire sur un repli. Le bouton
       est déjà tenu, ceci ferme la course du double-déclenchement. */
    if (readInProgress || readRate || companiesUnread) return;

    /**
     * ⚠️ SUR LES VALEURS EFFECTIVES, celles que l'écriture emportera — `valeurDe`, la
     * même que le champ affiche. Valider la seule saisie du moment reprocherait un vide
     * à un champ déjà rempli la fois d'avant, et laisserait passer l'inverse.
     *
     * Tous les fautifs d'un coup, le focus sur le premier : voir `allInvalid`.
     */
    if (step.key === "informations" && fields.length > 0) {
      // À partir d'ici, la phrase du bas a le droit de parler : on a essayé.
      setTried(true);
    }
    /**
     * ⚠️ A COMPANY STILL TO CHOOSE SENDS BACK TO ITS STEP, before anything is written or
     * created. A new person cannot get past that step without answering: this is for
     * someone reopened on their form who has none among the bearer's companies (an
     * older companion). What was typed stays, the step is only a step back.
     */
    if (step.key === "informations" && companyMissing && hasCompanyStep) {
      setCompanyComplaint(true);
      onGo(funnel.findIndex((s) => s.key === COMPANY_STEP.key));
      return;
    }
    const missingHere = step.key === COMPANY_STEP.key && companyMissing;
    const [first] = invalid;
    if (first || missingHere) {
      setComplaints(invalid.map((i) => i.complaint));
      setCompanyComplaint(missingHere);
      /* Le premier message peut être hors écran : le focus l'y ramène. */
      document
        .getElementById(first ? `f-${first.complaint.id}` : COMPANY_CHOICE)
        ?.focus();
      return;
    }
    setComplaints([]);
    /**
     * ⚠️ NOTHING IS WRITTEN FOR SOMEONE WHO DOES NOT EXIST YET: their company waits in
     * this screen's state for the form, which creates them and attaches them at once.
     * « Annuler » in between leaves nobody behind.
     */
    if (companionId === null && step.key === COMPANY_STEP.key) {
      advance();
      return;
    }
    /* On ne peut pas remplir une étape pour quelqu'un qui n'existe pas : seule la
       première a le droit de s'exécuter sans identifiant, et c'est elle qui le crée. */
    if (companionId === null && step.key !== "informations") return;
    setSending(true);
    setRate(false);
    try {
      if (step.key === COMPANY_STEP.key && companionId !== null) {
        await attachCompany(companionId);
      }
      if (step.key === "dates" && companionId !== null) {
        await writeDays(
          event.domainId,
          event.eventId,
          companionId,
          takenDays,
          days.map((d) => d.id),
        );
        await stale([
          "participation",
          event.domainId,
          event.eventId,
          companionId,
        ]);
      }
      /**
       * ⚠️ LA PREMIÈRE ÉTAPE CRÉE LA PERSONNE, les autres la complètent. C'est ce qui
       * permet d'entrer dans ce parcours avant qu'elle existe : on ouvre le fil, on
       * remplit son formulaire, et c'est « Continuer » qui la fait naître. Le parent
       * garde la main sur la création — voir `onCreer`.
       */
      if (step.key === "informations" && companionId === null) {
        const cree = await onCreate(typed);
        if (cree === null) {
          setSending(false);
          return;
        }
        try {
          await attachCompany(cree);
        } catch {
          /**
           * ⚠️ THE PERSON EXISTS FROM HERE ON, ONLY THEIR COMPANY IS MISSING. We stay on
           * the step and say so: the parent already knows them (`onCreate`), so the
           * next « Continuer » goes the « Modifier » way and only retries what failed,
           * instead of creating them twice. What was typed is dropped for their saved
           * answers: an address that matched an existing account was NOT written onto
           * it (`routes/Companions.tsx`), and the retry must not write it either.
           */
          setTyped({});
          setRate(true);
          setSending(false);
          return;
        }
        advance();
        setSending(false);
        return;
      }

      if (
        step.key === "informations" &&
        companionId !== null &&
        fields.length > 0
      ) {
        await saveAnswers({
          domainId: event.domainId,
          target: { eventId: event.eventId, userId: companionId },
          fields,
          values: Object.fromEntries(
            fields.map((f) => [
              f.id,
              f.id in typed ? typed[f.id] : valueOf(alreadyAnswers.data ?? {}, f),
            ]),
          ),
          current: alreadyAnswers.data ?? {},
          /* ⚠️ LA TYPOLOGIE DU PORTEUR, À CHAQUE ENVOI DE SON FORMULAIRE : c'est ce que
             fait la V1 (`screens/steps/Informations.jsx`), création comme modification.
             Un accompagnant inscrit sans elle la reçoit ici. */
          ...(heritage.typoId !== undefined
            ? { base: { UE: { typoId: heritage.typoId } } }
            : {}),
        });
        await stale([
          "form-answers",
          event.domainId,
          event.eventId,
          companionId,
        ]);
        /* Le formulaire porte aussi son NOM : la liste d'où l'on vient l'affiche, et
           elle mentirait jusqu'au prochain rechargement. */
        await stale(["accompagnants"]);
      }
      if (step.key === "informations" && companionId !== null) {
        await attachCompany(companionId);
      }
      if (step.key === "articles" && companionId !== null) {
        /* Même règle que le porteur : un article d'un jour non retenu ne part pas sur
           la facture, et un article masqué au back office n'est pas réécrit. */
        await saveSelection(
          event.domainId,
          event.eventId,
          companionId,
          cleanSelection(cart, { selectedDays: takenDays, known: articles ?? [] }),
        );
        await stale([
          "articles-retenus",
          event.domainId,
          event.eventId,
          companionId,
        ]);
        /* ⚠️ ET LE SOLDE DU PORTEUR : le serveur facture TOUT ce qui est inscrit sous
           lui (`UE.registeredBy2`), accompagnants compris. Un billet ajouté ici change
           donc ce que LUI doit — et son écran de paiement l'ignorait. */
        await stale(["articles-resume"]);
      }
      /* Les conférences s'écrivent au clic, créneau par créneau : une place se prend, et
         la garder en mémoire jusqu'ici la promettrait à deux personnes. Rien à envoyer
         en quittant l'étape. */
      advance();
    } catch {
      setRate(true);
    } finally {
      setSending(false);
    }
  };

  /* La question et le refus sont au `ConferencePicker`, comme pour le porteur : un
     refus REMONTE jusqu'à lui, qui sait dire « pris » ou « maximum atteint ». */
  const takeSlot = async (slot: Slot) => {
    if (!event || sending || companionId === null) return;
    setSending(true);
    setRate(false);
    try {
      const changes = slotChanges(conferences?.conferences ?? [], slot.id);
      await placeSlot(event.domainId, event.eventId, companionId, slot.id);
      logSlotChanges(event, changes, companionId);
    } finally {
      /* Aussi quand ça échoue : un refus veut dire que la liste affichée est fausse.
         Voir `routes/Conferences`. */
      await client.invalidateQueries({ queryKey: ["conferences"] });
      setSending(false);
    }
  };

  /* Ce qu'on lui propose : les jours qu'on vient de retenir POUR LUI, et non ceux du
     porteur — c'est le sens même d'un parcours décorrélé. */
  const stalls = salesDays(
    articles ?? [],
    days
      .filter((d) => takenDays.includes(d.id))
      .map((d) => ({ id: d.id, label: longDay(d.day, locale) })),
    /* Ce qu'il a déjà : un article acheté puis masqué doit rester lisible et retirable. */
    cart,
  );

  const programDays = programme(conferences?.conferences ?? [], locale, t);
  const conferencesLimit = maxAnnounce(conferences?.max ?? null, t);

  /**
   * CE QU'UN CHAMP AFFICHE — ce qu'on tape, puis ce que CETTE PERSONNE a déjà répondu.
   *
   * ⚠️ RIEN DU PORTEUR N'Y ENTRE, ET C'EST UNE RÈGLE. Un formulaire porte des données
   * personnelles : recopiées depuis quelqu'un d'autre, ce sont des données que personne
   * n'a vérifiées, et elles partent ensuite sur un badge et dans un fichier de
   * participants. Les autres étapes, elles, reprennent — ce sont des choix logistiques,
   * pas une identité.
   */
  const valueOfField = (field: Field) =>
    field.id in typed
      ? typed[field.id]
      : valueOf(alreadyAnswers.data ?? {}, field);

  /* Ce qui empêche encore cette étape de partir, recalculé au rendu : la phrase du bas ne
     nomme que ce qui manque encore, et s'efface d'elle-même. Voir `Informations.tsx`, où
     la règle est écrite. Ces champs-là ne se verrouillent pas : on remplit la fiche de
     quelqu'un qui n'a encore rien répondu. */
  const invalid =
    step.key === "informations" ? allInvalid(fields, valueOfField) : [];

  return (
    <div className="form" data-cfg="companionSteps">
      <div className="ttl">
        <h3>
          {name
            ? t("FOR_SOMEBODY", "Pour $1", [name])
            : t("ONE_MORE_PERSON", "Une personne de plus")}
        </h3>
      </div>

      {/**
       * LE FIL DE SES ÉTAPES — subordonné à celui du porteur, jamais confondu avec lui.
       *
       * ⚠️ IL EST EN LIGNE ET COMPACT, là où le fil principal occupe une colonne : deux
       * rails de même facture l'un sous l'autre, et l'on ne sait plus lequel dit où l'on
       * est dans SON inscription. Ici on remplit pour QUELQU'UN D'AUTRE, et cette
       * parenthèse doit se lire comme telle.
       *
       * ⚠️ ON REVIENT EN ARRIÈRE, PAS EN AVANT — la même règle que le tunnel, pour la
       * même raison : ce qui est devant dépend de ce qu'on n'a pas encore rempli. Les
       * étapes franchies restent ouvertes, chacune sur ce qui a déjà été enregistré.
       */}
      {funnel.length > 1 && (
        <ol className="substeps">
          {funnel.map((s, i) => (
            <li key={s.key}>
              <button
                type="button"
                className={`substep${i === index ? " now" : i < index ? " done" : ""}`}
                aria-current={i === index ? "step" : undefined}
                disabled={i >= index || sending}
                onClick={() => onGo(i)}
              >
                <i>{i < index ? <Icon name="check" /> : i + 1}</i>
                <b>{t(s.nameKey, s.name)}</b>
              </button>
            </li>
          ))}
        </ol>
      )}

      {rate && (
        <p className="loginerr" role="alert">
          {t("STEP_NOT_SAVED", "Cette étape n'a pas pu être enregistrée. Réessayez.")}
        </p>
      )}

      {step.key === "informations" && intro.trim() !== "" && (
        <Prose
          html={intro}
          className="se-prose"
          data-cfg="companionForm"
          />
      )}

      {/**
       * ⚠️ LES CHAMPS S'ANCRENT SUR « SON FORMULAIRE », PAS SUR SON PARCOURS. Sans
       * ancre propre, le bloc le plus proche était `companionSteps` : cliquer un champ
       * de l'accompagnant ouvrait la carte des INTERRUPTEURS de ses étapes (ses jours,
       * sa billetterie) — jamais le formulaire qu'on avait sous le curseur. L'étape
       * informations du porteur ancre les siens de la même façon (`infoForm`).
       */}
      {step.key === "informations" && words && (
        <div className="fgrid" data-cfg="companionForm">
          {visibleFields(fields, valueOfField).map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              words={words}
              locked={false}
              value={valueOfField(field)}
              error={complaints.find((r) => r.id === field.id)?.message}
              /* Le reproche tombe dès qu'on retouche le champ visé, pas les autres. */
              onChange={(v) => {
                setComplaints((rs) => rs.filter((r) => r.id !== field.id));
                setTyped((before) => ({ ...before, [field.id]: v }));
              }}
            />
          ))}
        </div>
      )}

      {/**
       * ═══ THEIR COMPANY ═══
       *
       * Several companies: its own step, the same choice as a radio question of the form
       * (`.fcase`), one per line, since a company name runs long. On the form, one line
       * recalls where they go: the only company, or the one chosen at the step before.
       *
       * ⚠️ ANCHORED ON THE STEP'S CARD, NOT ON « SON FORMULAIRE »: this question is not
       * one of the form's fields, it comes with the settings of the companions step.
       *
       * ⚠️ AND NEVER BEHIND THE FORM'S WORDS (`words`), which only exist with a form: a
       * coworker chosen from the list may have none to fill, and still needs a company.
       */}
      {step.key === "informations" && companyFlow && chosenName && (
        <p className="hint" data-cfg="companionsTitle">
          {t("COMPANION_COMPANY_ONLY", "Société de rattachement : $1", [
            chosenName,
          ])}
        </p>
      )}
      {step.key === COMPANY_STEP.key && companyFlow && offered.length > 0 && (
        <div
          className="f f--pleine"
          data-cfg="companionsTitle"
          id={COMPANY_CHOICE}
          tabIndex={-1}
          role="radiogroup"
          aria-label={t("COMPANION_COMPANY_LABEL", "Société de rattachement")}
          aria-invalid={companyComplaint || undefined}
          aria-describedby={
            companyComplaint ? `${COMPANY_CHOICE}-err` : undefined
          }
        >
          <span className="f__t">
            {t("COMPANION_COMPANY_LABEL", "Société de rattachement")}
            <i> *</i>
          </span>
          <div className="fchoix fchoix--col">
            {offered.map((company) => (
              <label className="fcase" key={company.id}>
                <input
                  type="radio"
                  name={COMPANY_CHOICE}
                  value={company.id}
                  checked={chosenCompany === company.id}
                  onChange={() => {
                    setPickedCompany(company.id);
                    setCompanyComplaint(false);
                  }}
                />
                <span>{company.name}</span>
              </label>
            ))}
          </div>
          {companyComplaint && (
            <p className="f__err" id={`${COMPANY_CHOICE}-err`} role="alert">
              {t(
                "COMPANION_COMPANY_REQUIRED",
                "Choisissez la société de cette personne.",
              )}
            </p>
          )}
        </div>
      )}

      {step.key === "dates" && (
        <ul className="opts">
          {days.map((day) => {
            const on = takenDays.includes(day.id);
            return (
              <li key={day.id}>
                <button
                  type="button"
                  className={`opt${on ? " on" : ""}`}
                  aria-pressed={on}
                  onClick={() =>
                    setPickedDays(
                      takenDays.includes(day.id)
                        ? takenDays.filter((d) => d !== day.id)
                        : [...takenDays, day.id],
                    )
                  }
                >
                  <span className="opt__c" aria-hidden="true">
                    {on && <Icon name="check" />}
                  </span>
                  <span className="opt__t">
                    <b>{longDay(day.day, locale)}</b>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* ⚠️ SEULEMENT SI UN ARTICLE EST VRAIMENT REPRIS : une commande faite de jours
          vides (`{ "0": [] }`) annonçait « Repris de votre commande » sur des cases
          toutes décochées (bug #200). */}
      {step.key === "articles" && hasArticles(reused) && (
        <p className="hint">
          {t(
            "REUSED_FROM_YOUR_ORDER",
            "Repris de votre commande, à ajuster. Ces articles seront ajoutés à votre facture.",
          )}
        </p>
      )}

      {step.key === "articles" && (
        <div className="arts">
          {stalls.length === 0 && (
            <p className="hint">{t("NOTHING_FOR_THOSE_DAYS", 'Rien à proposer pour les jours retenus.')}</p>
          )}
          {stalls.map((day) => (
            <div className="artday" key={day.id}>
              {day.label && (
                <div className="artday__h">
                  <b>{day.label}</b>
                  {day.singleChoice && <span>{t("ONE_CHOICE_ONLY", 'Un seul choix')}</span>}
                </div>
              )}
              {day.articles.map((article) => {
                const on = isSelected(cart, day.id, article.id);
                return (
                  <button
                    type="button"
                    key={article.id}
                    className={`art${on ? " on" : ""}`}
                    aria-pressed={on}
                    onClick={() =>
                      setPickedCart(toggle(cart, day, article.id))
                    }
                  >
                    <span
                      className={day.singleChoice ? "opt__r" : "opt__c"}
                      aria-hidden="true"
                    >
                      {on && <Icon name="check" />}
                    </span>
                    <span className="art__t">
                      <b>{article.label}</b>
                      <ArticlePrice article={article} />
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {step.key === "conferences" && conferencesLimit && (
        <p className="hint">{conferencesLimit}</p>
      )}

      {step.key === "conferences" && (
        <ConferencePicker
          key={companionId ?? "new"}
          days={programDays}
          who="companion"
          busy={sending}
          place={takeSlot}
        >
          {days.length === 0 && (
            <p className="hint">{t("NO_CONFERENCE_FOR_THEM", 'Aucune conférence à lui proposer.')}</p>
          )}
        </ConferencePicker>
      )}

      {readRate && (
        <p className="loginerr" role="alert">
          {t(
            "PREVIOUS_CHOICES_UNREADABLE",
            "Vos choix précédents n'ont pas pu être relus. Rechargez la page avant de continuer, pour ne pas les remplacer.",
          )}
        </p>
      )}

      {companiesUnread && (
        <p className="loginerr" role="alert">
          {t(
            "COMPANIES_NOT_LOADED",
            "Vos sociétés n'ont pas pu être lues. Rechargez la page.",
          )}
        </p>
      )}

      {/* Ce que le bouton ne dit pas : il reste cliquable, et le clic est le
          diagnostic. Voir `resteAFaire`. */}
      {tried && invalid.length > 0 && words && (
        <p className="hint" role="status">
          <Remaining invalid={invalid} words={words} />
        </p>
      )}

      <div className="actions">
        {/* ⚠️ LA SEULE SORTIE DE LA PARENTHÈSE, hors aller au bout. Le parcours masque la
            liste et les boutons de l'étape : sans elle, changer d'avis ou buter sur un
            champ enfermait le porteur dedans (bug #339). Pâle et AVANT « Continuer »,
            qui ferme la rangée comme sur toutes les étapes. Coupée pendant un envoi :
            une création partie ne s'annule pas, et la personne surgirait dans la liste
            qu'on vient de retrouver. */}
        <Button
          type="button"
          variant="ghost"
          disabled={sending}
          onClick={onCancel}
        >
          {t("CANCEL", "Annuler")}
        </Button>
        <Button
          type="button"
          loading={sending || readInProgress}
          disabled={readRate || companiesUnread}
          onClick={() => void valid()}
        >
          {/* On dit où l'on va : « Continuer » sur la dernière étape laisse croire
              qu'il en reste. */}
          {remaining > 0
            ? t("CONTINUE", "Continuer")
            : t("FINISH_FOR_THIS_PERSON", "Terminer pour cette personne")}
        </Button>
      </div>
    </div>
  );
}
